<template>
  <ListComponent title="Workload">
    <template #actions>
      <v-btn
        color="primary"
        outlined
        depressed
        class="mr-2"
        @click="loadDashboard()"
      >
        <v-icon left small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
      <v-btn color="primary" depressed @click="showCreateDialog = true">
        <v-icon left small>mdi-plus</v-icon>
        Create Workload
      </v-btn>
    </template>

    <template #list>
      <v-row class="ma-0" dense>
        <v-col cols="12" md="9" class="pa-0">
          <v-row class="ma-0" dense>
            <!-- Container Status + Financials -->
            <v-col cols="12" md="7" class="pa-2">
              <v-card outlined class="fill-height">
                <div class="section-head">
                  <span class="dot orange"></span>
                  Container Status — All Locations
                </div>
                <div class="stat-strip">
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text">Total Target</div>
                    <div class="text-h5 font-weight-bold">
                      {{ overview.total_target }}
                    </div>
                    <div class="caption grey--text">Planned containers</div>
                    <v-progress-linear
                      :value="100"
                      color="grey lighten-1"
                      height="3"
                      class="mt-2"
                    />
                  </div>
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text">Completed</div>
                    <div class="text-h5 font-weight-bold success--text">
                      {{ overview.completed }}
                    </div>
                    <div class="caption grey--text">Gate out + gate in</div>
                  </div>
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text">In Transit</div>
                    <div class="text-h5 font-weight-bold warning--text">
                      {{ overview.in_transit }}
                    </div>
                    <div class="caption grey--text">Awaiting gate-in</div>
                  </div>
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text">Remaining</div>
                    <div class="text-h5 font-weight-bold success--text">
                      {{ overview.remaining }}
                    </div>
                    <div class="caption grey--text">Yet to move</div>
                    <v-progress-linear
                      :value="remainingPct"
                      color="success"
                      height="3"
                      class="mt-2"
                    />
                  </div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="5" class="pa-2">
              <v-card outlined class="fill-height">
                <div class="section-head">
                  <span class="dot green"></span>
                  Financials — Actual (Moved Containers)
                </div>
                <div class="stat-strip">
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text d-flex align-center text-no-wrap">
                      <span>Revenue</span>
                      <v-tooltip top max-width="280">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            small
                            class="ml-1"
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>
                          This is based on data from TOS. Actual cost may vary once
                          the subhire job or invoice is created.
                        </span>
                      </v-tooltip>
                    </div>
                    <div class="text-h6 font-weight-bold success--text">
                      {{ formatCurrency(overview.revenue) }}
                    </div>
                    <div class="caption grey--text">
                      Estimated Revenue
                    </div>
                  </div>
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text d-flex align-center text-no-wrap">
                      <span>Cost</span>
                      <v-tooltip top max-width="280">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            small
                            class="ml-1"
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>
                          This is based on data from TOS. Actual cost may vary once
                          the subhire job or invoice is created.
                        </span>
                      </v-tooltip>
                    </div>
                    <div class="text-h6 font-weight-bold error--text">
                      {{ formatCurrency(overview.cost) }}
                    </div>
                    <div class="caption grey--text">Handling + subhire</div>
                  </div>
                  <div class="stat-tile-bordered">
                    <div class="overline grey--text">Margin</div>
                    <div class="text-h6 font-weight-bold primary--text">
                      {{ formatCurrency(overview.margin) }}
                    </div>
                    <div class="caption grey--text">
                      Profit · {{ marginPct }}% of revenue
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- Location Wise Status -->
            <v-col cols="12" class="pa-2">
              <v-card outlined>
                <div class="section-head d-flex align-center">
                  Location Wise Status
                  <v-spacer />
                  <span class="caption grey--text mr-3"
                    >Click a location to expand</span
                  >
                  <v-btn
                    small
                    color="primary"
                    depressed
                    @click="showCreateDialog = true"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Create Workload
                  </v-btn>
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Location</th>
                      <th>Tracking Start</th>
                      <th>Types</th>
                      <th class="text-right">Total</th>
                      <th class="text-right">Remaining</th>
                      <th class="text-right">In Transit</th>
                      <th class="text-right">Completed</th>
                      <th class="text-right">Revenue</th>
                      <th class="text-right">Cost</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(row, i) in paginatedLocations">
                      <tr
                        :key="row.location"
                        class="location-row"
                        :class="{
                          'location-row--active': expandedLocations.includes(
                            row.location,
                          ),
                        }"
                        :style="{
                          borderLeftColor: pieColors[i % pieColors.length],
                        }"
                        @click="toggleLocation(row.location)"
                      >
                        <td style="width: 24px">
                          <v-icon small>{{
                            expandedLocations.includes(row.location)
                              ? "mdi-chevron-down"
                              : "mdi-chevron-right"
                          }}</v-icon>
                        </td>
                        <td class="font-weight-bold">
                          {{ row.location_name }}
                        </td>
                        <td class="caption grey--text">
                          {{ formatDate(row.tracking_start_date) }}
                        </td>
                        <td>
                          <v-chip
                            v-for="type in row.container_types"
                            :key="type"
                            x-small
                            outlined
                            class="mr-1"
                          >
                            {{ type }}
                          </v-chip>
                        </td>
                        <td class="text-right">{{ row.total_target }}</td>
                        <td class="text-right success--text font-weight-bold">
                          {{ row.remaining }}
                        </td>
                        <td class="text-right warning--text font-weight-bold">
                          {{ row.in_transit }}
                        </td>
                        <td class="text-right">{{ row.completed }}</td>
                        <td class="text-right success--text">
                          {{ formatCurrency(row.revenue) }}
                        </td>
                        <td class="text-right error--text">
                          {{ formatCurrency(row.cost) }}
                        </td>
                        <td @click.stop>
                          <v-btn
                            x-small
                            outlined
                            color="primary"
                            @click="openAddContainers(row)"
                          >
                            + Add Containers
                          </v-btn>
                        </td>
                      </tr>
                      <template v-if="expandedLocations.includes(row.location)">
                        <tr
                          v-for="item in row.items"
                          :key="`${row.location}-${item.container_type_name}`"
                          class="location-item-row"
                        >
                          <td></td>
                          <td></td>
                          <td>
                            <v-chip x-small outlined>{{
                              item.container_type_name
                            }}</v-chip>
                          </td>
                          <td class="text-right">{{ item.total_target }}</td>
                          <td class="text-right success--text">
                            {{ item.remaining }}
                          </td>
                          <td class="text-right warning--text">
                            {{ item.in_transit }}
                          </td>
                          <td class="text-right">{{ item.completed }}</td>
                          <td class="text-right success--text">
                            {{ formatCurrency(item.revenue) }}
                          </td>
                          <td class="text-right error--text">
                            {{ formatCurrency(item.cost) }}
                          </td>
                          <td></td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </v-simple-table>
                <div
                  v-if="!locations.length"
                  class="pa-6 text-center caption grey--text"
                >
                  No workloads tracked yet.
                </div>
                <div
                  class="d-flex justify-center py-2"
                  v-if="locationsPageCount > 1"
                >
                  <v-pagination
                    v-model="locationsPage"
                    :length="locationsPageCount"
                    total-visible="7"
                  />
                </div>
              </v-card>
            </v-col>

            <!-- Daily Gate-Out Volume + Remaining Share -->
            <v-col cols="12" md="8" class="pa-2">
              <v-card outlined>
                <div class="section-head d-flex align-center">
                  Daily Gate-Out Volume
                  <v-spacer />
                  <span class="caption grey--text mr-2">From</span>
                  <DateField
                    :inputProps="{
                      dense: true,
                      outlined: true,
                      hideDetails: 'auto',
                      style: 'width: 130px',
                    }"
                    class="mr-2 date-range-field"
                    v-model="dateRange.start"
                    @change="loadDashboard"
                  />
                  <span class="caption grey--text mr-2">To</span>
                  <DateField
                    :inputProps="{
                      dense: true,
                      outlined: true,
                      hideDetails: 'auto',
                      style: 'width: 130px',
                    }"
                    class="mr-2 date-range-field"
                    v-model="dateRange.end"
                    @change="loadDashboard"
                  />
                  <span class="caption grey--text mr-2"
                    >Click a bar to drill in</span
                  >
                  <v-btn
                    x-small
                    outlined
                    color="primary"
                    @click="openExportDialog"
                  >
                    <v-icon x-small left>mdi-download</v-icon>
                    Export
                  </v-btn>
                </div>
                <v-card-text>
                  <div class="chart-wrap" ref="barWrap">
                    <canvas ref="barChart" @click="onBarClick"></canvas>
                  </div>
                  <div class="d-flex align-center mt-2" style="gap: 16px">
                    <span class="caption d-flex align-center"
                      ><span
                        class="legend-dot"
                        style="background: #14b8a6"
                      ></span
                      >Internal</span
                    >
                    <span class="caption d-flex align-center"
                      ><span
                        class="legend-dot"
                        style="background: #f59e0b"
                      ></span
                      >External</span
                    >
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="pa-2">
              <v-card outlined class="fill-height">
                <div class="section-head">Remaining Share</div>
                <v-card-text class="d-flex align-center" style="gap: 16px">
                  <div
                    class="chart-wrap"
                    style="width: 140px; height: 140px"
                    ref="pieWrap"
                  >
                    <canvas ref="pieChart"></canvas>
                    <div class="donut-center">
                      <div class="text-h6 font-weight-bold">
                        {{ overview.remaining }}
                      </div>
                      <div class="caption grey--text">Remaining</div>
                    </div>
                  </div>
                  <div>
                    <div
                      v-for="(row, i) in locations"
                      :key="row.location"
                      class="d-flex align-center mb-1"
                    >
                      <span
                        class="legend-dot"
                        :style="{ background: pieColors[i % pieColors.length] }"
                      ></span>
                      <span class="caption mr-2">{{ row.location_name }}</span>
                      <span class="caption font-weight-bold">{{
                        row.remaining
                      }}</span>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Date Wise -->
            <v-col cols="12" class="pa-2">
              <v-card outlined>
                <div class="section-head d-flex align-center">
                  Date Wise
                  <span class="caption grey--text ml-2"
                    >Click a row to drill in</span
                  >
                  <v-spacer />
                  <v-select
                    :items="locationFilterOptions"
                    item-text="text"
                    item-value="value"
                    v-model="dateWiseLocationFilter"
                    dense
                    outlined
                    hide-details="auto"
                    class="date-wise-filter"
                    @change="loadDashboard"
                  />
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th
                        v-for="code in dateWiseLocationCodes"
                        :key="code"
                        class="text-right"
                      >
                        {{ code }}
                      </th>
                      <th class="text-right">Total</th>
                      <th class="text-right">Int.</th>
                      <th class="text-right">Ext.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in paginatedDateWise"
                      :key="row.date"
                      class="location-row"
                    >
                      <td @click="openDrillIn(row.date)">
                        {{ formatDate(row.date) }}
                      </td>
                      <td
                        v-for="code in dateWiseLocationCodes"
                        :key="code"
                        class="text-right"
                        :class="
                          row.locations[code]
                            ? 'cursor-pointer'
                            : 'cursor-default'
                        "
                        @click="
                          row.locations[code] && openDrillIn(row.date, code)
                        "
                      >
                        {{ row.locations[code] || "–" }}
                      </td>
                      <td class="text-right font-weight-bold cursor-default">
                        {{ row.total }}
                      </td>
                      <td class="text-right success--text cursor-default">
                        {{ row.internal }}
                      </td>
                      <td class="text-right warning--text cursor-default">
                        {{ row.external }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div
                  v-if="!dateWise.length"
                  class="pa-6 text-center caption grey--text"
                >
                  No gate-out activity yet.
                </div>
                <div
                  class="d-flex justify-center py-2"
                  v-if="dateWisePageCount > 1"
                >
                  <v-pagination
                    v-model="dateWisePage"
                    :length="dateWisePageCount"
                    total-visible="7"
                  />
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" md="3" class="pa-2">
          <v-card outlined class="fill-height">
            <div class="section-head">
              Workload Log
              <span class="caption grey--text ml-2"
                >{{ logs.length }} entries</span
              >
            </div>
            <v-card-text class="pa-3 log-panel">
              <v-timeline
                dense
                align-top
                v-if="logs.length"
                class="log-timeline"
              >
                <v-timeline-item
                  v-for="log in logs"
                  :key="log.id"
                  small
                  color="orange"
                  fill-dot
                >
                  <div class="caption font-weight-bold orange--text mb-1">
                    {{ formatDate(log.created) }}
                  </div>
                  <div class="log-entry pa-2">
                    <div class="d-flex justify-space-between align-start">
                      <div class="d-flex align-center" style="gap: 6px">
                        <v-chip x-small outlined v-if="log.location_name">{{
                          log.location_name
                        }}</v-chip>
                        <v-chip
                          x-small
                          color="success"
                          text-color="white"
                          class="font-weight-bold"
                          v-if="qtyDelta(log.message)"
                        >
                          {{ qtyDelta(log.message) }}
                        </v-chip>
                      </div>
                      <span class="caption grey--text">{{
                        formatDate(log.created, true)
                      }}</span>
                    </div>
                    <div class="caption mt-1">
                      {{ logMessageText(log.message) }}
                    </div>
                    <div class="caption grey--text mt-1" v-if="log.updated_by">
                      {{ log.updated_by }}
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
              <div
                v-if="!logs.length"
                class="pa-6 text-center caption grey--text"
              >
                No activity yet.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <CreateWorkloadDialog
        v-model="showCreateDialog"
        @workloadCreated="loadDashboard"
      />
      <AddContainersDialog
        v-model="showAddContainersDialog"
        :location="selectedLocation"
        @containersAdded="loadDashboard"
      />
      <WorkloadDrillInDialog
        v-model="showDrillInDialog"
        :date="selectedDrillInDate"
        :locationCode="selectedDrillInLocationCode"
      />

      <v-dialog max-width="440px" v-model="showExportDialog" persistent>
        <v-card>
          <v-card-title class="secondary white--text py-3 px-5">
            <span class="text-subtitle-1 font-weight-bold"
              >Export Daily Gate-Out Volume</span
            >
            <v-spacer />
            <v-btn icon color="white" small @click="showExportDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-5 pt-4">
            <v-row dense>
              <v-col cols="6">
                <DateField
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'From *',
                    'hide-details': 'auto',
                  }"
                  v-model="exportRange.start"
                />
              </v-col>
              <v-col cols="6">
                <DateField
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'To *',
                    'hide-details': 'auto',
                  }"
                  v-model="exportRange.end"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider class="mt-3" />
          <v-card-actions class="px-5 py-3">
            <v-spacer />
            <v-btn
              depressed
              outlined
              @click="showExportDialog = false"
              class="mr-2"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              depressed
              :loading="exporting"
              :disabled="!exportRange.start || !exportRange.end"
              @click="exportDailyGateOutVolume"
            >
              <v-icon small class="mr-1">mdi-download</v-icon>
              Export
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import Chart from "chart.js";
import * as XLSX from "xlsx";
import CreateWorkloadDialog from "@/components/ModuleBased/dialogForms/CreateWorkloadDialog.vue";
import AddContainersDialog from "@/components/ModuleBased/dialogForms/AddContainersDialog.vue";
import WorkloadDrillInDialog from "@/components/ModuleBased/dialogs/WorkloadDrillInDialog.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import { isAuthenticated, hasRouteAccess } from "@/utils/functions";

const PIE_COLORS = [
  "#f97316",
  "#14b8a6",
  "#3b82f6",
  "#a855f7",
  "#f59e0b",
  "#ef4444",
];

export default {
  components: {
    ListComponent,
    CreateWorkloadDialog,
    AddContainersDialog,
    WorkloadDrillInDialog,
    DateField,
  },
  data() {
    return {
      overview: {
        total_target: 0,
        completed: 0,
        in_transit: 0,
        remaining: 0,
        revenue: 0,
        cost: 0,
        margin: 0,
      },
      locations: [],
      dailyGateOutVolume: [],
      dateWise: [],
      dateWiseLocationCodes: [],
      logs: [],
      expandedLocations: [],
      showCreateDialog: false,
      showAddContainersDialog: false,
      showDrillInDialog: false,
      selectedLocation: null,
      selectedDrillInDate: null,
      selectedDrillInLocationCode: null,
      dateRange: { start: "", end: "" },
      dateRangeInitialized: false,
      dateWiseLocationFilter: null,
      locationsPage: 1,
      locationsPerPage: 5,
      dateWisePage: 1,
      dateWisePerPage: 7,
      showExportDialog: false,
      exportRange: { start: "", end: "" },
      exporting: false,
      pieColors: PIE_COLORS,
      _bar: null,
      _pie: null,
    };
  },
  computed: {
    locationFilterOptions() {
      return [
        { text: "All Locations", value: null },
        ...this.locations.map((row) => ({
          text: row.location_name,
          value: row.location,
        })),
      ];
    },
    remainingPct() {
      if (!this.overview.total_target) return 0;
      return Math.min(
        100,
        (this.overview.remaining / this.overview.total_target) * 100,
      );
    },
    marginPct() {
      if (!this.overview.revenue) return 0;
      return Math.round((this.overview.margin / this.overview.revenue) * 100);
    },
    locationsPageCount() {
      return Math.ceil(this.locations.length / this.locationsPerPage) || 1;
    },
    paginatedLocations() {
      const start = (this.locationsPage - 1) * this.locationsPerPage;
      return this.locations.slice(start, start + this.locationsPerPage);
    },
    dateWisePageCount() {
      return Math.ceil(this.dateWise.length / this.dateWisePerPage) || 1;
    },
    paginatedDateWise() {
      const start = (this.dateWisePage - 1) * this.dateWisePerPage;
      return this.dateWise.slice(start, start + this.dateWisePerPage);
    },
  },
  methods: {
    defaultDateRange(locations) {
      const toIso = (date) => date.toISOString().slice(0, 10);
      const end = new Date();
      const earliestCap = new Date();
      earliestCap.setDate(earliestCap.getDate() - 30);

      const trackingDates = (locations || [])
        .map(
          (row) => row.tracking_start_date && new Date(row.tracking_start_date),
        )
        .filter(Boolean);
      const oldest = trackingDates.length
        ? new Date(Math.min(...trackingDates.map((date) => date.getTime())))
        : earliestCap;
      const start = oldest < earliestCap ? earliestCap : oldest;

      return { start: toIso(start), end: toIso(end) };
    },
    formatCurrency(value) {
      const num = Number(value || 0);
      const sign = num < 0 ? "-" : "";
      const abs = Math.abs(num);

      if (abs >= 1000000) {
        return `${sign}AED ${(abs / 1000000).toFixed(1)}M`;
      }
      if (abs >= 1000) {
        return `${sign}AED ${(abs / 1000).toFixed(1)}K`;
      }
      return `${sign}AED ${abs.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })}`;
    },
    formatDate(value) {
      return this.$globals.formatDate(value);
    },
    formatShortDate(value) {
      if (!value) return "";
      const date = new Date(value);
      const day = `${date.getDate()}`.padStart(2, "0");
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const year = `${date.getFullYear()}`.slice(-2);
      return `${day}/${month}/${year}`;
    },
    qtyDelta(message) {
      const match = (message || "").match(/^(\+\d+)/);
      return match ? match[1] : null;
    },
    logMessageText(message) {
      return (message || "").replace(/^\+\d+\s*/, "");
    },
    openExportDialog() {
      this.exportRange = {
        start: this.dateRange.start,
        end: this.dateRange.end,
      };
      this.showExportDialog = true;
    },
    exportDailyGateOutVolume() {
      if (!this.exportRange.start || !this.exportRange.end) return;
      this.exporting = true;
      this.$api.workload
        .getWorkloadDashboard({
          start_date: this.exportRange.start,
          end_date: this.exportRange.end,
        })
        .then((res) => {
          const data = res.data || res;
          const volume = data.daily_gate_out_volume || [];
          if (!volume.length) {
            this.$bus.$emit("showToastMessage", {
              message: "No gate-out activity in the selected date range.",
              color: "error",
            });
            return;
          }
          const rows = volume.map((row) => ({
            Date: this.formatDate(row.date),
            Internal: row.internal,
            External: row.external,
            Total: row.internal + row.external,
          }));
          const worksheet = XLSX.utils.json_to_sheet(rows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Daily Gate-Out Volume",
          );
          const from = this.formatShortDate(this.exportRange.start);
          const to = this.formatShortDate(this.exportRange.end);
          XLSX.writeFile(
            workbook,
            `daily-gate-out-volume-${from}-to-${to}.xlsx`.replace(/\//g, "-"),
          );
          this.showExportDialog = false;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Failed to export data for the selected range.",
            color: "error",
          });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    toggleLocation(locationId) {
      const idx = this.expandedLocations.indexOf(locationId);
      if (idx === -1) this.expandedLocations.push(locationId);
      else this.expandedLocations.splice(idx, 1);
    },
    openAddContainers(row) {
      this.selectedLocation = row;
      this.showAddContainersDialog = true;
    },
    openDrillIn(date, locationCode = null) {
      this.selectedDrillInDate = date;
      this.selectedDrillInLocationCode = locationCode;
      this.showDrillInDialog = true;
    },
    onBarClick(event) {
      if (!this._bar) return;
      const points = this._bar.getElementsAtEventForMode(event, "index", {
        intersect: false,
      });
      if (points.length) {
        const index = points[0]._index;
        const date = this.dailyGateOutVolume[index]?.date;
        if (date) this.openDrillIn(date);
      }
    },
    loadDashboard() {
      this.$bus.$emit("showLoader", true);
      const params = {};
      if (this.dateRange.start) params.start_date = this.dateRange.start;
      if (this.dateRange.end) params.end_date = this.dateRange.end;
      if (this.dateWiseLocationFilter)
        params.location = this.dateWiseLocationFilter;

      this.$api.workload
        .getWorkloadDashboard(params)
        .then((res) => {
          const data = res.data || res;
          this.overview = data.overview;
          this.locations = data.locations;
          this.dailyGateOutVolume = data.daily_gate_out_volume;
          this.dateWise = data.date_wise;
          this.dateWiseLocationCodes = data.date_wise_location_codes || [];
          this.logs = data.logs || [];
          this.locationsPage = 1;
          this.dateWisePage = 1;

          if (!this.dateRangeInitialized) {
            this.dateRangeInitialized = true;
            this.dateRange = this.defaultDateRange(data.locations);
            this.loadDashboard();
            return;
          }

          this.$nextTick(() => {
            this.renderBar();
            this.renderPie();
          });
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    renderBar() {
      if (this._bar) {
        this._bar.destroy();
        this._bar = null;
      }
      const wrap = this.$refs.barWrap;
      const canvas = this.$refs.barChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = 220;

      const labels = this.dailyGateOutVolume.map((row) =>
        this.formatShortDate(row.date),
      );
      const internal = this.dailyGateOutVolume.map((row) => row.internal);
      const external = this.dailyGateOutVolume.map((row) => row.external);

      this._bar = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Internal",
              backgroundColor: "#14b8a6",
              stack: "s",
              data: internal,
            },
            {
              label: "External",
              backgroundColor: "#f59e0b",
              stack: "s",
              data: external,
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [
              {
                stacked: true,
                gridLines: { display: false },
                ticks: { fontSize: 9, fontColor: "#94a3b8", maxRotation: 0 },
              },
            ],
            yAxes: [
              {
                stacked: true,
                gridLines: { color: "#e2e8f0" },
                ticks: { fontSize: 9, fontColor: "#94a3b8" },
              },
            ],
          },
          tooltips: { mode: "index", intersect: false },
        },
      });
    },
    renderPie() {
      if (this._pie) {
        this._pie.destroy();
        this._pie = null;
      }
      const wrap = this.$refs.pieWrap;
      const canvas = this.$refs.pieChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = wrap.clientHeight;

      const hasData = this.locations.some((row) => row.remaining > 0);
      const labels = hasData
        ? this.locations.map((row) => row.location_name)
        : ["No Data"];
      const data = hasData ? this.locations.map((row) => row.remaining) : [1];
      const colors = hasData
        ? this.locations.map((_, i) => PIE_COLORS[i % PIE_COLORS.length])
        : ["#e2e8f0"];

      this._pie = new Chart(canvas.getContext("2d"), {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: colors,
              borderWidth: 2,
              borderColor: "#fff",
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: { display: false },
          cutoutPercentage: 68,
        },
      });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      this.loadDashboard();
    }
  },
  beforeDestroy() {
    if (this._bar) this._bar.destroy();
    if (this._pie) this._pie.destroy();
  },
};
</script>

<style scoped>
.section-head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.stat-strip {
  display: flex;
  flex-wrap: wrap;
}
.stat-tile-bordered {
  flex: 1 1 0;
  min-width: 120px;
  padding: 14px 16px;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
.stat-tile-bordered:last-child {
  border-right: none;
}
.stat-tile-bordered .overline {
  font-size: 11px;
  letter-spacing: 0.05em;
}
.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.dot.orange {
  background-color: #f97316;
}
.dot.green {
  background-color: #16a34a;
}
.location-row {
  cursor: pointer;
  border-left: 3px solid transparent;
}
.location-row:hover {
  background-color: #fafafa;
}
.location-row--active {
  background-color: #fff3e0;
}
.location-row--active:hover {
  background-color: #fff3e0;
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-default {
  cursor: default;
}
.location-item-row {
  background-color: #fafafa;
}
.location-item-row td {
  font-size: 12px;
}
.chart-wrap {
  width: 100%;
  height: 220px;
  position: relative;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
}
.donut-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}
.date-range-field {
  display: inline-block;
}
.date-wise-filter {
  max-width: 160px;
  font-size: 12px;
}
.date-wise-filter >>> .v-input__control {
  min-height: 28px !important;
}
.date-wise-filter >>> .v-input__slot {
  min-height: 28px !important;
  padding: 0 8px !important;
}
.date-wise-filter >>> .v-select__selection {
  font-size: 12px;
}
.log-panel {
  max-height: calc(100vh - 260px);
  overflow-y: auto;
}
.log-entry {
  background-color: #fafafa;
  border-radius: 6px;
}
</style>
